import React, { useEffect, useState } from 'react';

import cn from 'classnames';

import { IconButton } from 'components';
import Modal from 'components/Modal';
import { ButtonV3 } from 'components/ComponentV2';
import { Radio } from 'components/form';
import { useSponsorCompaniesInternal } from 'hooks/useSponsorCompaniesInternal';
import { useSponsorshipPlans } from 'pages/sponsorships/useSponsorshipPlans';
import {
  UpdateSponsorshipPlanAttrs,
  UpdateSponsorshipPlanPayload,
} from 'lib/models/sponsorship-plans';
import { CustomFeatures } from '../CustomFeatures/CustomFeatures.component';

import styles from './SelectPlanModal.module.scss';

export interface SponsorUpdatePlanDataType {
  currentPlanId?: string;
  displayCustomFeatures?: boolean;
  sponsorshipId?: string;
  inConversation?: boolean;
  refreshData?: boolean;
  customFeatures?: string[];
  huzzleFeatures?: string[];
}
interface SelectPlanModalProps {
  open: boolean;
  sponsorData?: SponsorUpdatePlanDataType;
  goToPlans: () => void;
  onClose: () => void;
}

export const SelectPlanModal: React.FC<SelectPlanModalProps> = (
  props: SelectPlanModalProps
) => {
  const { open, sponsorData, goToPlans, onClose } = props;
  const { sponsorshipPlans, huzzleFeatures } = useSponsorshipPlans();
  const { isLoading, updateSponsorPlan } = useSponsorCompaniesInternal();

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [showCustomFeatures, setShowCustomFeatures] = useState<boolean>(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [otherFeatures, setOtherFeatures] = useState<string[]>(['']);

  useEffect(() => {
    if (!open) {
      setSelectedPlanId('');
      setShowCustomFeatures(false);
      setSelectedFeatures([]);
      setOtherFeatures(['']);
    } else {
      setShowCustomFeatures(!!sponsorData?.displayCustomFeatures);
    }
  }, [open]);

  useEffect(() => {
    if (sponsorData?.currentPlanId) {
      setSelectedPlanId(sponsorData.currentPlanId);
    }
  }, [sponsorData]);

  const onSelectPlan = (planId: string) => () => {
    setSelectedPlanId(planId);
  };

  const toggleCustomFeatures = () => {
    setShowCustomFeatures((isVisible) => !isVisible);
    setSelectedFeatures(sponsorData?.huzzleFeatures || []);
    setOtherFeatures(sponsorData?.customFeatures || ['']);
  };

  const onSelectHuzzleFeatures = (values: Array<string>) => {
    setSelectedFeatures(values);
  };

  const onOtherFeaturesChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const tempFeatures = otherFeatures.slice(0);
    tempFeatures[index] = event.target.value;
    setOtherFeatures(tempFeatures);
  };

  const onAddSponsor = async () => {
    const payloadAttrs: UpdateSponsorshipPlanAttrs = {
      sponsorship_plan_attributes: showCustomFeatures
        ? {
            custom: true,
            custom_features: otherFeatures,
            huzzle_feature_ids: selectedFeatures.map(
              (value: string) => huzzleFeatures![value]
            ),
          }
        : undefined,
      sponsorship_plan_id: showCustomFeatures ? undefined : selectedPlanId,
    };
    const payload: UpdateSponsorshipPlanPayload = sponsorData?.inConversation
      ? {
          sponsorship_invitation: payloadAttrs,
        }
      : {
          sponsorship: payloadAttrs,
        };

    const response = await updateSponsorPlan(
      sponsorData?.sponsorshipId || '',
      payload,
      !!sponsorData?.inConversation,
      !!sponsorData?.refreshData
    );
    if (response) {
      onClose();
    }
  };

  const renderEmptyPlans = () => (
    <div className={styles.SelectPlanModalContentEmpty}>
      <div className={styles.emptyInfo}>
        <p className={styles.emoji}>😓</p>
        <p className={styles.emptyTitle}>{"You don't have any plans"}</p>
      </div>
      <p className={styles.maxPlans}>
        Create up to 3 pricing plans for attracting companies to sponsor your
        society.
      </p>
      <div className={styles.emptyAction}>
        <ButtonV3 size="large" onClick={goToPlans}>
          Create a plan
        </ButtonV3>
        <ButtonV3
          size="large"
          variant="text"
          color="black"
          onClick={toggleCustomFeatures}
        >
          Select custom features
        </ButtonV3>
      </div>
    </div>
  );

  const renderPlans = () => {
    if (sponsorshipPlans && sponsorshipPlans.length > 0) {
      return (
        <div className={styles.sponsorshipPlans}>
          {sponsorshipPlans?.map((plan) => (
            <div
              key={plan.id}
              className={cn(styles.sponsorshipPlansRadio, {
                [styles.selected]: plan.id === selectedPlanId,
              })}
              onClick={onSelectPlan(plan.id)}
            >
              <div>
                <p className={styles.planName}>{plan.title}</p>
                <p className={styles.planDesc}>
                  {[...plan.huzzle_features, ...plan.custom_features].join(
                    ', '
                  )}
                </p>
              </div>
              <Radio
                checked={plan.id === selectedPlanId}
                className={styles.radioSelect}
              />
            </div>
          ))}
        </div>
      );
    } else {
      return renderEmptyPlans();
    }
  };

  const renderCustomFeatures = () => (
    <CustomFeatures
      selectedHuzzleFeatures={selectedFeatures}
      onSelectHuzzleFeatures={onSelectHuzzleFeatures}
      otherSelectedFeatures={otherFeatures}
      onOtherFeaturesChange={onOtherFeaturesChange}
      onAddRow={() =>
        setOtherFeatures((otherFeatures) => [...otherFeatures, ''])
      }
    />
  );

  return (
    <Modal
      isOpen={open}
      onRequestClose={() => onClose()}
      contentClassName={styles.SelectPlanModal}
    >
      <div className={styles.SelectPlanModalHeader}>
        {showCustomFeatures && !sponsorData?.displayCustomFeatures && (
          <p className={styles.goBack} onClick={toggleCustomFeatures}>
            <IconButton iconName="arrow-button-left" size="small" />
            Back
          </p>
        )}
        {sponsorshipPlans && sponsorshipPlans.length > 0 && (
          <p className={styles.headerTitle}>
            Select {showCustomFeatures ? 'features' : 'plan'}
          </p>
        )}
        <IconButton
          className={styles.closeIcon}
          iconName="close"
          size="small"
          onClick={() => onClose()}
        />
      </div>

      <div className={styles.SelectPlanModalContent}>
        {showCustomFeatures ? renderCustomFeatures() : renderPlans()}
      </div>

      {((sponsorshipPlans && sponsorshipPlans.length > 0) ||
        showCustomFeatures) && (
        <div className={styles.SelectPlanModalAction}>
          {!showCustomFeatures && (
            <ButtonV3 color="secondary" onClick={toggleCustomFeatures}>
              Select custom features
            </ButtonV3>
          )}
          <ButtonV3
            color="primary"
            onClick={onAddSponsor}
            disabled={
              showCustomFeatures
                ? selectedFeatures.length === 0
                : !selectedPlanId
            }
            isLoading={isLoading}
          >
            Add sponsor
          </ButtonV3>
        </div>
      )}
    </Modal>
  );
};
